import { FileText } from 'lucide-react';
import type { Patient, RadiologyTargetLesion } from '../../../types';
import { Markdown } from '../../../components/Markdown/Markdown';
import { formatDisplayDate } from '../../../utils/displayDate';
import './RadiologySummary.scss';

interface RadiologySummaryProps {
  patient: Patient;
}

/**
 * Format a radiology date using the dashboard display convention.
 *
 * @param value - Date string from the extraction
 * @returns A locale date string, the raw value, or an em dash
 */
function formatDate(value?: string): string {
  return formatDisplayDate(value, '—');
}

/**
 * Parse the leading signed number out of a percentage-change string so the cell
 * can be colored (negative = tumor shrinkage = good).
 *
 * @param change - A change string like "-26%" or "increased 12%"
 * @returns The parsed number, or null when no number is present
 */
function parseChange(change: string): number | null {
  const match = (change || '').match(/-?\d+(\.\d+)?/);
  return match ? parseFloat(match[0]) : null;
}

/**
 * Radiology document summary. Renders the structured RECIST extraction produced
 * by the summarizer (study info, impression, target-lesion measurements, findings
 * and prior-study comparison). Falls back to the raw narrative when no structured
 * radiology data is present.
 *
 * @param patient - The patient hydrated with `radiology` from the document summary
 */
export function RadiologySummary({ patient }: RadiologySummaryProps) {
  const radiology = patient.radiology;
  const narrative = patient.narrative;

  if (!radiology) {
    return (
      <div className="radiology-section">
        <h3 className="radiology-section-title">Report summary</h3>
        <div className="radiology-impression-box">
          <Markdown className="radiology-narrative">
            {narrative || 'No radiology summary available for this patient.'}
          </Markdown>
        </div>
      </div>
    );
  }

  const lesions: RadiologyTargetLesion[] = radiology.targetLesions || [];
  const additionalFindings = [
    radiology.lymphNodes && `Lymph nodes: ${radiology.lymphNodes}`,
    radiology.newLesions && `New lesions: ${radiology.newLesions}`,
    ...(radiology.urgentFindings || []),
  ].filter(Boolean) as string[];

  return (
    <div className="radiology-report">
      {/* Report Header */}
      <div className="radiology-report-header">
        <div className="radiology-report-title">
          <FileText className="radiology-report-icon" />
          <span className="radiology-report-title-text">Report summary</span>
          <span className="radiology-report-subtitle">
            {radiology.studyType || patient.documentName} - {formatDate(radiology.studyDate)}
          </span>
        </div>
      </div>

      {/* Study Information Section */}
      <div className="radiology-study-info">
        <div className="radiology-study-grid">
          <div>
            <p className="radiology-study-label">Study type</p>
            <p className="radiology-study-value">{radiology.studyType || 'N/A'}</p>
          </div>
          <div>
            <p className="radiology-study-label">Study date</p>
            <p className="radiology-study-value">{formatDate(radiology.studyDate)}</p>
          </div>
          <div>
            <p className="radiology-study-label">Prior comparison</p>
            <p className="radiology-study-value">{radiology.priorComparison || 'N/A'}</p>
          </div>
          <div>
            <p className="radiology-study-label">Clinical Indication</p>
            <p className="radiology-study-value">{radiology.clinicalIndication || 'N/A'}</p>
          </div>
          <div>
            <p className="radiology-study-label">Response criteria</p>
            <p className="radiology-study-value">{radiology.responseCriteria || 'N/A'}</p>
          </div>
        </div>
      </div>

      {/* Impression Section */}
      {radiology.impression.length > 0 && (
        <div className="radiology-section">
          <h3 className="radiology-section-title">Impression</h3>
          <div className="radiology-impression-box">
            <ul className="radiology-impression-list">
              {radiology.impression.map((impression, index) => (
                <li key={index} className="radiology-impression-item">
                  {impression}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* RECIST Measurements Section */}
      {lesions.length > 0 && (
        <div className="radiology-section">
          <h3 className="radiology-section-title">RECIST Measurements</h3>
          <div className="radiology-recist-container">
            <div className="radiology-recist-header">
              <span className="radiology-treatment-label">Target lesions</span>
              <span className="radiology-treatment-date">{formatDate(radiology.studyDate)}</span>
            </div>
            <div className="radiology-table-wrapper">
              <table className="radiology-recist-table">
                <thead>
                  <tr>
                    <th className="radiology-th-lesion">Lesion</th>
                    <th className="radiology-th-measurement">Baseline</th>
                    <th className="radiology-th-measurement">Current</th>
                    <th className="radiology-th-change">Change</th>
                  </tr>
                </thead>
                <tbody>
                  {lesions.map((lesion, index) => {
                    const pct = parseChange(lesion.change);
                    const changeCls =
                      pct === null
                        ? ''
                        : pct < 0
                          ? 'radiology-change-negative'
                          : pct > 0
                            ? 'radiology-change-positive'
                            : '';
                    return (
                      <tr key={index}>
                        <td className="radiology-td-lesion">{lesion.location || '—'}</td>
                        <td className="radiology-td-measurement">{lesion.prior || '—'}</td>
                        <td className="radiology-td-measurement">{lesion.current || '—'}</td>
                        <td className={`radiology-td-change ${changeCls}`}>
                          {lesion.change || lesion.changeText || '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Additional Findings Section */}
      {additionalFindings.length > 0 && (
        <div className="radiology-section">
          <h3 className="radiology-section-title">Additional Findings</h3>
          <div className="radiology-findings-box">
            <ul className="radiology-findings-list">
              {additionalFindings.map((finding, index) => (
                <li key={index} className="radiology-finding-item">
                  {finding}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Comparative Analysis (prior studies) */}
      {radiology.studies.length > 1 && (
        <div className="radiology-section radiology-section-with-separator">
          <h3 className="radiology-section-title">Comparative Analysis</h3>
          <div className="radiology-table-wrapper">
            <table className="radiology-recist-table">
              <thead>
                <tr>
                  <th className="radiology-th-lesion">Study date</th>
                  <th className="radiology-th-measurement">Study</th>
                  <th className="radiology-th-change">Response</th>
                </tr>
              </thead>
              <tbody>
                {radiology.studies.map((study, index) => (
                  <tr key={index}>
                    <td className="radiology-td-lesion">{formatDate(study.date)}</td>
                    <td className="radiology-td-measurement">{study.studyType || '—'}</td>
                    <td className="radiology-td-change">{study.overallResponse || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Comparative trend summary (the reduced, scale-aware narrative across studies) — last */}
      {narrative && (
        <div className="radiology-section radiology-section-with-separator">
          <h3 className="radiology-section-title">Comparative Trend Summary</h3>
          <div className="radiology-impression-box">
            <Markdown className="radiology-narrative">{narrative}</Markdown>
          </div>
        </div>
      )}
    </div>
  );
}
